
import {defineComponent, type PropType} from "vue";
import router from "@/router";

type PropagationTreeNode = {
  id: number,
  name: string,
  isCombinedRecipe: boolean,
  children: PropagationTreeNode[],
}

type PropagationSelectionState = {
  isChecked: boolean,
  isIndeterminate: boolean,
}

type PropagationChangePreview = {
  changeKey: string,
  label: string,
  kind: "added" | "removed" | "existing",
}

const MAY_CONTAIN_CHANGE_KEY = "may-contain";

export default defineComponent({
  name: "AllergenPropagationTree",
  props: {
    nodes: {
      type: Array as PropType<PropagationTreeNode[]>,
      required: true
    },
    selectionStateById: {
      type: null as unknown as PropType<Map<number, PropagationSelectionState>>,
      required: true,
      validator: (value: unknown) => value instanceof Map
    },
    changePreviewByTargetId: {
      type: null as unknown as PropType<Map<number, PropagationChangePreview[]>>,
      required: true,
      validator: (value: unknown) => value instanceof Map
    },
    disabledChangeIdsByTargetId: {
      type: Object as PropType<Record<number, string[]>>,
      required: true
    },
    root: {
      type: Boolean,
      default: false
    }
  },
  emits: ["toggle", "toggle-change"],
  setup(props, { emit }) {
    function isChecked(nodeId: number) {
      return props.selectionStateById.get(nodeId)?.isChecked || false;
    }

    function isIndeterminate(nodeId: number) {
      return props.selectionStateById.get(nodeId)?.isIndeterminate || false;
    }

    function onCheckboxChanged(nodeId: number, event: Event) {
      emit("toggle", {
        targetId: nodeId,
        checked: (event.target as HTMLInputElement).checked
      });
    }

    function onChildToggle(payload: { targetId: number, checked: boolean }) {
      emit("toggle", payload);
    }

    function getPreviewChanges(targetId: number) {
      return props.changePreviewByTargetId.get(targetId) || [];
    }

    function isMayContainChange(changeKey: string) {
      return changeKey === MAY_CONTAIN_CHANGE_KEY || changeKey.startsWith(`${MAY_CONTAIN_CHANGE_KEY}:`);
    }

    function getAllergenPreviewChanges(targetId: number) {
      return getPreviewChanges(targetId).filter((change) => !isMayContainChange(change.changeKey));
    }

    function getMayContainPreviewChanges(targetId: number) {
      const mayContainChanges = getPreviewChanges(targetId).filter((change) => isMayContainChange(change.changeKey));
      const prioritizedKinds: Record<PropagationChangePreview["kind"], number> = {
        removed: 3,
        existing: 2,
        added: 1
      };
      const dedupedByLabel = new Map<string, PropagationChangePreview>();

      for (const change of mayContainChanges) {
        const existing = dedupedByLabel.get(change.label);

        if (!existing || prioritizedKinds[change.kind] > prioritizedKinds[existing.kind]) {
          dedupedByLabel.set(change.label, change);
        }
      }

      return Array.from(dedupedByLabel.values());
    }

    function isChangeDisabled(targetId: number, changeKey: string) {
      return (props.disabledChangeIdsByTargetId[targetId] || []).includes(changeKey);
    }

    function onChangeToggle(targetId: number, changeKey: string) {
      emit("toggle-change", { targetId, changeKey });
    }

    function onChildChangeToggle(payload: { targetId: number, changeKey: string }) {
      emit("toggle-change", payload);
    }

    function openTargetInNewTab(node: PropagationTreeNode) {
      const route = router.resolve({
        name: node.isCombinedRecipe ? "dishesEdit" : "recipesEdit",
        params: { id: `${node.id}` }
      });

      window.open(route.href, "_blank", "noopener");
    }

    function getOpenTargetLabel(node: PropagationTreeNode) {
      return node.isCombinedRecipe
        ? "Open combined recipe"
        : "Open recipe";
    }

    function getOpenTargetTitle(node: PropagationTreeNode) {
      return node.isCombinedRecipe
        ? "Open this combined recipe in a new tab"
        : "Open this recipe in a new tab";
    }

    function getChangeIconClass(kind: PropagationChangePreview["kind"]) {
      switch (kind) {
      case "added":
        return "fa-check";
      case "removed":
        return "fa-times";
      default:
        return "fa-minus";
      }
    }

    return {
      isChecked,
      isIndeterminate,
      onCheckboxChanged,
      onChildToggle,
      getPreviewChanges,
      getAllergenPreviewChanges,
      getMayContainPreviewChanges,
      isChangeDisabled,
      onChangeToggle,
      onChildChangeToggle,
      openTargetInNewTab,
      getOpenTargetLabel,
      getOpenTargetTitle,
      getChangeIconClass
    };
  }
});
